/**
 * FikoCatiaDimension - CATIA V5 Professional Dimension System
 *
 * CATIA V5 tarzında profesyonel teknik çizim ve ölçülendirme sistemi.
 * - Yeşil ölçü çizgileri
 * - Toleranslı değerler (+0.25/-0.15 formatı)
 * - Profesyonel CATIA tarzı oklar
 * - Çap ölçüleri (Ø sembolü)
 * - Uzatma çizgileri (extension lines)
 * - Referans noktaları ve işaretçiler
 *
 * @author FikoYT CAD Team
 * @version 1.0.0 - CATIA Professional
 */

import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { getCanvasResolutionVec2 } from '../../../FikoCanvasResolution';

// ============================================================================
// ENUMS & TYPES
// ============================================================================

/**
 * CATIA Ölçü Tipleri
 */
export enum CatiaDimensionType {
    LINEAR = 'LINEAR',               // Doğrusal ölçü
    HORIZONTAL = 'HORIZONTAL',       // Yatay ölçü
    VERTICAL = 'VERTICAL',           // Dikey ölçü
    ALIGNED = 'ALIGNED',             // Hizalı ölçü
    ANGULAR = 'ANGULAR',             // Açısal ölçü
    DIAMETER = 'DIAMETER',           // Çap ölçüsü (Ø)
    RADIUS = 'RADIUS',               // Yarıçap ölçüsü (R)
    CHAMFER = 'CHAMFER',             // Pah ölçüsü
    ORDINATE = 'ORDINATE',           // Koordinat ölçüsü
    ARC_LENGTH = 'ARC_LENGTH'        // Yay uzunluğu
}

/**
 * Tolerans Tipi
 */
export enum ToleranceType {
    NONE = 'NONE',                   // Tolerans yok
    SYMMETRIC = 'SYMMETRIC',         // ±0.25
    ASYMMETRIC = 'ASYMMETRIC',       // +0.25/-0.15
    LIMITS = 'LIMITS',               // 100.25 / 99.85
    FIT = 'FIT'                      // H7/g6
}

/**
 * Ok Stili
 */
export enum CatiaArrowStyle {
    FILLED = 'FILLED',               // Dolu üçgen (CATIA default)
    OPEN = 'OPEN',                   // Açık üçgen
    DOT = 'DOT',                     // Nokta
    TICK = 'TICK',                   // Çizgi (architectural)
    NONE = 'NONE'                    // Ok yok
}

/**
 * Metin Pozisyonu
 */
export enum TextPosition {
    ABOVE = 'ABOVE',                 // Çizginin üstünde
    BELOW = 'BELOW',                 // Çizginin altında
    CENTER = 'CENTER',               // Çizgi üzerinde (break)
    INLINE = 'INLINE'                // Çizgi ile aynı hizada
}

/**
 * Tolerans Konfigürasyonu
 */
export interface ToleranceConfig {
    type: ToleranceType;
    upperValue?: number;             // Üst tolerans değeri
    lowerValue?: number;             // Alt tolerans değeri
    fitClass?: string;               // H7, g6, etc.
    precision: number;               // Ondalık basamak
}

/**
 * CATIA Stil Konfigürasyonu
 */
export interface CatiaStyleConfig {
    // Renkler
    dimensionColor: number;          // Ölçü çizgisi rengi
    textColor: string;               // Metin rengi
    toleranceColor: string;          // Tolerans rengi
    extensionColor: number;          // Uzatma çizgisi rengi

    // Çizgi
    lineWidth: number;               // Çizgi kalınlığı
    extensionOvershoot: number;      // Uzatma çizgisi taşma miktarı
    extensionGap: number;            // Uzatma çizgisi başlangıç boşluğu
    dimensionOffset: number;         // Ölçü çizgisi offset'i

    // Ok
    arrowStyle: CatiaArrowStyle;
    arrowLength: number;             // Ok uzunluğu
    arrowWidth: number;              // Ok genişliği

    // Metin
    fontSize: number;
    fontFamily: string;
    textPosition: TextPosition;
    textGap: number;                 // Metin ile çizgi arası boşluk

    // Tolerans
    toleranceFontSize: number;       // Tolerans yazı boyutu (ana yazıdan küçük)
    showTolerance: boolean;

    // Genel
    precision: number;               // Ondalık basamak sayısı
    unit: string;                    // Birim (mm, in, etc.)
    showUnit: boolean;
    prefix: string;                  // Ön ek (Ø, R, etc.)
}

/**
 * CATIA Dimension Seçenekleri
 */
export interface CatiaDimensionOptions {
    type: CatiaDimensionType;
    style: Partial<CatiaStyleConfig>;
    tolerance: Partial<ToleranceConfig>;
    value?: number;                  // Manuel değer (otomatik hesaplanmazsa)
    text?: string;                   // Özel metin
}

// ============================================================================
// DEFAULT VALUES - CATIA V5 PROFESSIONAL STYLE
// ============================================================================

/**
 * CATIA V5 Varsayılan Stil
 */
export const CATIA_DEFAULT_STYLE: CatiaStyleConfig = {
    // Renkler - CATIA yeşili
    dimensionColor: 0x00cc00,        // Parlak yeşil
    textColor: '#00cc00',            // Yeşil metin
    toleranceColor: '#00cc00',       // Yeşil tolerans
    extensionColor: 0x00cc00,        // Yeşil uzatma çizgisi

    // Çizgi
    lineWidth: 1.5,
    extensionOvershoot: 2,           // mm
    extensionGap: 1.5,               // mm
    dimensionOffset: 10,             // mm

    // Ok - CATIA tarzı dolu üçgen
    arrowStyle: CatiaArrowStyle.FILLED,
    arrowLength: 3,                  // mm
    arrowWidth: 1.2,                 // mm

    // Metin
    fontSize: 12,
    fontFamily: 'Arial, sans-serif',
    textPosition: TextPosition.ABOVE,
    textGap: 1,

    // Tolerans
    toleranceFontSize: 9,
    showTolerance: true,

    // Genel
    precision: 3,
    unit: 'mm',
    showUnit: false,
    prefix: ''
};

/**
 * Varsayılan Tolerans
 */
export const DEFAULT_TOLERANCE: ToleranceConfig = {
    type: ToleranceType.ASYMMETRIC,
    upperValue: 0.25,
    lowerValue: 0.15,
    precision: 2
};

// ============================================================================
// MAIN CLASS
// ============================================================================

/**
 * FikoCatiaDimension - CATIA V5 Profesyonel Ölçü Sistemi
 */
export class FikoCatiaDimension extends THREE.Group {
    readonly dimensionType = 'FikoCatiaDimension';

    // Noktalar
    private startPoint: THREE.Vector3 = new THREE.Vector3();
    private endPoint: THREE.Vector3 = new THREE.Vector3();
    private offsetDirection: THREE.Vector3 = new THREE.Vector3(0, 1, 0);

    // Ayarlar
    private options: CatiaDimensionOptions;
    private style: CatiaStyleConfig;
    private tolerance: ToleranceConfig;

    // Bileşenler
    private dimensionLine: Line2 | null = null;
    private extensionLine1: Line2 | null = null;
    private extensionLine2: Line2 | null = null;
    private arrow1: THREE.Mesh | null = null;
    private arrow2: THREE.Mesh | null = null;
    private labelSprite: THREE.Sprite | null = null;
    private toleranceSprite: THREE.Sprite | null = null;
    private referenceMarkers: THREE.Mesh[] = [];

    // Değerler
    private _value: number = 0;
    private _displayText: string = '';

    // ========================================================================
    // CONSTRUCTOR
    // ========================================================================

    constructor(
        start: THREE.Vector3,
        end: THREE.Vector3,
        options: Partial<CatiaDimensionOptions> = {}
    ) {
        super();

        this.startPoint.copy(start);
        this.endPoint.copy(end);

        // Varsayılan ayarları birleştir
        this.options = {
            type: options.type || CatiaDimensionType.LINEAR,
            style: options.style || {},
            tolerance: options.tolerance || {}
        };

        this.style = { ...CATIA_DEFAULT_STYLE, ...this.options.style };
        this.tolerance = { ...DEFAULT_TOLERANCE, ...this.options.tolerance };

        // Değeri hesapla
        this._value = options.value ?? this.calculateValue();
        this._displayText = options.text ?? this.formatValue();

        // Render ayarları
        this.renderOrder = 1000;
        this.frustumCulled = false;

        // Oluştur
        this.build();
    }

    // ========================================================================
    // PUBLIC API
    // ========================================================================

    /**
     * Değeri al
     */
    public get value(): number {
        return this._value;
    }

    /**
     * Değeri ayarla
     */
    public set value(val: number) {
        this._value = val;
        this._displayText = this.formatValue();
        this.updateLabel();
    }

    /**
     * Noktaları güncelle
     */
    public setPoints(start: THREE.Vector3, end: THREE.Vector3): void {
        this.startPoint.copy(start);
        this.endPoint.copy(end);
        this._value = this.calculateValue();
        this._displayText = this.formatValue();
        this.rebuild();
    }

    /**
     * Offset yönünü ayarla
     */
    public setOffsetDirection(direction: THREE.Vector3): void {
        this.offsetDirection.copy(direction).normalize();
        this.rebuild();
    }

    /**
     * Stili güncelle
     */
    public updateStyle(style: Partial<CatiaStyleConfig>): void {
        this.style = { ...this.style, ...style };
        this.rebuild();
    }

    /**
     * Toleransı güncelle
     */
    public updateTolerance(tolerance: Partial<ToleranceConfig>): void {
        this.tolerance = { ...this.tolerance, ...tolerance };
        this.updateLabel();
    }

    /**
     * Toleransı aç/kapat
     */
    public showTolerance(show: boolean): void {
        this.style.showTolerance = show;
        this.updateLabel();
    }

    /**
     * Prefix ayarla (Ø, R, etc.)
     */
    public setPrefix(prefix: string): void {
        this.style.prefix = prefix;
        this._displayText = this.formatValue();
        this.updateLabel();
    }

    /**
     * Yeniden oluştur
     */
    public rebuild(): void {
        this.dispose();
        this.build();
    }

    /**
     * Temizlik
     */
    public dispose(): void {
        // Dimension line
        if (this.dimensionLine) {
            this.dimensionLine.geometry.dispose();
            (this.dimensionLine.material as THREE.Material).dispose();
            this.remove(this.dimensionLine);
            this.dimensionLine = null;
        }

        // Extension lines
        [this.extensionLine1, this.extensionLine2].forEach(line => {
            if (line) {
                line.geometry.dispose();
                (line.material as THREE.Material).dispose();
                this.remove(line);
            }
        });
        this.extensionLine1 = null;
        this.extensionLine2 = null;

        // Arrows
        [this.arrow1, this.arrow2].forEach(arrow => {
            if (arrow) {
                arrow.geometry.dispose();
                (arrow.material as THREE.Material).dispose();
                this.remove(arrow);
            }
        });
        this.arrow1 = null;
        this.arrow2 = null;

        // Labels
        if (this.labelSprite) {
            const mat = this.labelSprite.material as THREE.SpriteMaterial;
            if (mat.map) mat.map.dispose();
            mat.dispose();
            this.remove(this.labelSprite);
            this.labelSprite = null;
        }

        if (this.toleranceSprite) {
            const mat = this.toleranceSprite.material as THREE.SpriteMaterial;
            if (mat.map) mat.map.dispose();
            mat.dispose();
            this.remove(this.toleranceSprite);
            this.toleranceSprite = null;
        }

        // Reference markers
        this.referenceMarkers.forEach(marker => {
            marker.geometry.dispose();
            (marker.material as THREE.Material).dispose();
            this.remove(marker);
        });
        this.referenceMarkers = [];
    }

    // ========================================================================
    // PRIVATE - BUILD
    // ========================================================================

    /**
     * Tüm bileşenleri oluştur
     */
    private build(): void {
        // Offset pozisyonlarını hesapla
        const offset = this.style.dimensionOffset;
        const direction = this.calculateOffsetDirection();

        const dimStart = this.startPoint.clone().add(direction.clone().multiplyScalar(offset));
        const dimEnd = this.endPoint.clone().add(direction.clone().multiplyScalar(offset));

        // Extension lines
        this.createExtensionLine(this.startPoint, dimStart, true);
        this.createExtensionLine(this.endPoint, dimEnd, false);

        // Dimension line
        this.createDimensionLine(dimStart, dimEnd);

        // Arrows
        this.createArrow(dimStart, dimEnd, true);
        this.createArrow(dimEnd, dimStart, false);

        // Label
        this.createLabel(dimStart, dimEnd);

        // Reference markers (opsiyonel)
        this.createReferenceMarkers();
    }

    /**
     * Offset yönünü hesapla
     */
    private calculateOffsetDirection(): THREE.Vector3 {
        const lineDir = this.endPoint.clone().sub(this.startPoint).normalize();

        // Perpendicular yön
        if (this.options.type === CatiaDimensionType.VERTICAL) {
            return new THREE.Vector3(1, 0, 0);
        } else if (this.options.type === CatiaDimensionType.HORIZONTAL) {
            return new THREE.Vector3(0, 1, 0);
        }

        // Otomatik hesapla
        const up = new THREE.Vector3(0, 0, 1);
        const perp = new THREE.Vector3().crossVectors(lineDir, up);

        if (perp.lengthSq() < 0.001) {
            perp.crossVectors(lineDir, new THREE.Vector3(0, 1, 0));
        }

        return perp.normalize();
    }

    /**
     * Uzatma çizgisi oluştur
     */
    private createExtensionLine(start: THREE.Vector3, end: THREE.Vector3, isFirst: boolean): void {
        const direction = end.clone().sub(start).normalize();

        // Gap uygula
        const gapStart = start.clone().add(direction.clone().multiplyScalar(this.style.extensionGap));

        // Overshoot uygula
        const overshootEnd = end.clone().add(direction.clone().multiplyScalar(this.style.extensionOvershoot));

        const positions = [
            gapStart.x, gapStart.y, gapStart.z,
            overshootEnd.x, overshootEnd.y, overshootEnd.z
        ];

        const geometry = new LineGeometry();
        geometry.setPositions(positions);

        const material = new LineMaterial({
            color: this.style.extensionColor,
            linewidth: this.style.lineWidth * 0.0008,
            resolution: getCanvasResolutionVec2()
        });

        const line = new Line2(geometry, material);
        line.computeLineDistances();

        if (isFirst) {
            this.extensionLine1 = line;
        } else {
            this.extensionLine2 = line;
        }

        this.add(line);
    }

    /**
     * Ölçü çizgisi oluştur
     */
    private createDimensionLine(start: THREE.Vector3, end: THREE.Vector3): void {
        const positions = [
            start.x, start.y, start.z,
            end.x, end.y, end.z
        ];

        const geometry = new LineGeometry();
        geometry.setPositions(positions);

        const material = new LineMaterial({
            color: this.style.dimensionColor,
            linewidth: this.style.lineWidth * 0.001,
            resolution: getCanvasResolutionVec2()
        });

        this.dimensionLine = new Line2(geometry, material);
        this.dimensionLine.computeLineDistances();
        this.add(this.dimensionLine);
    }

    /**
     * Ok oluştur
     */
    private createArrow(position: THREE.Vector3, target: THREE.Vector3, isFirst: boolean): void {
        if (this.style.arrowStyle === CatiaArrowStyle.NONE) return;

        const direction = target.clone().sub(position).normalize();
        const length = this.style.arrowLength;
        const width = this.style.arrowWidth;

        let geometry: THREE.BufferGeometry;

        switch (this.style.arrowStyle) {
            case CatiaArrowStyle.FILLED:
                geometry = this.createFilledArrowGeometry(length, width);
                break;
            case CatiaArrowStyle.OPEN:
                geometry = this.createOpenArrowGeometry(length, width);
                break;
            case CatiaArrowStyle.DOT:
                geometry = new THREE.CircleGeometry(width * 0.5, 16);
                break;
            case CatiaArrowStyle.TICK:
                geometry = this.createTickGeometry(length);
                break;
            default:
                geometry = this.createFilledArrowGeometry(length, width);
        }

        const material = new THREE.MeshBasicMaterial({
            color: this.style.dimensionColor,
            side: THREE.DoubleSide
        });

        const arrow = new THREE.Mesh(geometry, material);
        arrow.position.copy(position);

        // Yönlendirme
        const angle = Math.atan2(direction.y, direction.x);
        arrow.rotation.z = angle;

        if (isFirst) {
            this.arrow1 = arrow;
        } else {
            this.arrow2 = arrow;
        }

        this.add(arrow);
    }

    /**
     * Dolu ok geometrisi (CATIA tarzı)
     */
    private createFilledArrowGeometry(length: number, width: number): THREE.BufferGeometry {
        const shape = new THREE.Shape();

        // CATIA tarzı sivri ok
        shape.moveTo(0, 0);
        shape.lineTo(-length, width / 2);
        shape.lineTo(-length * 0.7, 0);
        shape.lineTo(-length, -width / 2);
        shape.closePath();

        return new THREE.ShapeGeometry(shape);
    }

    /**
     * Açık ok geometrisi
     */
    private createOpenArrowGeometry(length: number, width: number): THREE.BufferGeometry {
        const points = [
            new THREE.Vector3(-length, width / 2, 0),
            new THREE.Vector3(0, 0, 0),
            new THREE.Vector3(-length, -width / 2, 0)
        ];

        return new THREE.BufferGeometry().setFromPoints(points);
    }

    /**
     * Tick (çizgi) geometrisi
     */
    private createTickGeometry(length: number): THREE.BufferGeometry {
        const points = [
            new THREE.Vector3(-length / 2, length / 2, 0),
            new THREE.Vector3(length / 2, -length / 2, 0)
        ];

        return new THREE.BufferGeometry().setFromPoints(points);
    }

    /**
     * Etiket oluştur
     */
    private createLabel(dimStart: THREE.Vector3, dimEnd: THREE.Vector3): void {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        // Canvas boyutunu ayarla
        canvas.width = 256;
        canvas.height = 128;

        // Temizle
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Ana değer
        ctx.font = `bold ${this.style.fontSize * 2}px ${this.style.fontFamily}`;
        ctx.fillStyle = this.style.textColor;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        const mainText = this._displayText;
        const mainY = this.style.showTolerance ? canvas.height / 2 - 15 : canvas.height / 2;
        ctx.fillText(mainText, canvas.width / 2, mainY);

        // Tolerans
        if (this.style.showTolerance && this.tolerance.type !== ToleranceType.NONE) {
            ctx.font = `${this.style.toleranceFontSize * 2}px ${this.style.fontFamily}`;
            ctx.fillStyle = this.style.toleranceColor;

            const toleranceText = this.formatTolerance();

            if (this.tolerance.type === ToleranceType.ASYMMETRIC) {
                // Üst tolerans
                const upperText = `+${this.tolerance.upperValue?.toFixed(this.tolerance.precision)}`;
                ctx.textAlign = 'left';
                ctx.fillText(upperText, canvas.width / 2 + 60, mainY - 8);

                // Alt tolerans
                const lowerText = `-${this.tolerance.lowerValue?.toFixed(this.tolerance.precision)}`;
                ctx.fillText(lowerText, canvas.width / 2 + 60, mainY + 12);
            } else {
                ctx.textAlign = 'center';
                ctx.fillText(toleranceText, canvas.width / 2, mainY + 25);
            }
        }

        // Texture oluştur
        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;

        const spriteMaterial = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: false
        });

        this.labelSprite = new THREE.Sprite(spriteMaterial);

        // Pozisyon - çizginin ortası
        const midPoint = dimStart.clone().add(dimEnd).multiplyScalar(0.5);
        const offsetDir = this.calculateOffsetDirection();

        // Text position'a göre ayarla
        let textOffset = 0;
        switch (this.style.textPosition) {
            case TextPosition.ABOVE:
                textOffset = this.style.textGap + 3;
                break;
            case TextPosition.BELOW:
                textOffset = -(this.style.textGap + 3);
                break;
            default:
                textOffset = this.style.textGap;
        }

        this.labelSprite.position.copy(midPoint).add(offsetDir.clone().multiplyScalar(textOffset));

        // Ölçekleme
        const scale = 15;
        this.labelSprite.scale.set(scale, scale * 0.5, 1);

        this.add(this.labelSprite);
    }

    /**
     * Referans işaretçileri oluştur
     */
    private createReferenceMarkers(): void {
        // Başlangıç ve bitiş noktalarında küçük işaretler
        const markerSize = 0.5;
        const geometry = new THREE.CircleGeometry(markerSize, 8);
        const material = new THREE.MeshBasicMaterial({
            color: this.style.dimensionColor,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0.5
        });

        [this.startPoint, this.endPoint].forEach(point => {
            const marker = new THREE.Mesh(geometry.clone(), material.clone());
            marker.position.copy(point);
            this.referenceMarkers.push(marker);
            this.add(marker);
        });
    }

    /**
     * Etiketi güncelle
     */
    private updateLabel(): void {
        if (!this.labelSprite) return;

        // Mevcut texture'ı güncelle
        const mat = this.labelSprite.material as THREE.SpriteMaterial;
        if (mat.map) {
            const canvas = (mat.map.image as HTMLCanvasElement);
            const ctx = canvas.getContext('2d')!;

            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Ana değer
            ctx.font = `bold ${this.style.fontSize * 2}px ${this.style.fontFamily}`;
            ctx.fillStyle = this.style.textColor;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            const mainText = this._displayText;
            const mainY = this.style.showTolerance ? canvas.height / 2 - 15 : canvas.height / 2;
            ctx.fillText(mainText, canvas.width / 2, mainY);

            // Tolerans
            if (this.style.showTolerance && this.tolerance.type !== ToleranceType.NONE) {
                ctx.font = `${this.style.toleranceFontSize * 2}px ${this.style.fontFamily}`;
                ctx.fillStyle = this.style.toleranceColor;

                if (this.tolerance.type === ToleranceType.ASYMMETRIC) {
                    const upperText = `+${this.tolerance.upperValue?.toFixed(this.tolerance.precision)}`;
                    ctx.textAlign = 'left';
                    ctx.fillText(upperText, canvas.width / 2 + 60, mainY - 8);

                    const lowerText = `-${this.tolerance.lowerValue?.toFixed(this.tolerance.precision)}`;
                    ctx.fillText(lowerText, canvas.width / 2 + 60, mainY + 12);
                } else {
                    ctx.textAlign = 'center';
                    ctx.fillText(this.formatTolerance(), canvas.width / 2, mainY + 25);
                }
            }

            mat.map.needsUpdate = true;
        }
    }

    // ========================================================================
    // PRIVATE - CALCULATIONS
    // ========================================================================

    /**
     * Değeri hesapla
     */
    private calculateValue(): number {
        switch (this.options.type) {
            case CatiaDimensionType.HORIZONTAL:
                return Math.abs(this.endPoint.x - this.startPoint.x);
            case CatiaDimensionType.VERTICAL:
                return Math.abs(this.endPoint.y - this.startPoint.y);
            default:
                return this.startPoint.distanceTo(this.endPoint);
        }
    }

    /**
     * Değeri formatla
     */
    private formatValue(): string {
        let text = this.style.prefix;
        text += this._value.toFixed(this.style.precision);

        if (this.style.showUnit) {
            text += ' ' + this.style.unit;
        }

        return text;
    }

    /**
     * Toleransı formatla
     */
    private formatTolerance(): string {
        switch (this.tolerance.type) {
            case ToleranceType.SYMMETRIC:
                return `±${this.tolerance.upperValue?.toFixed(this.tolerance.precision)}`;

            case ToleranceType.ASYMMETRIC:
                return `+${this.tolerance.upperValue?.toFixed(this.tolerance.precision)} / -${this.tolerance.lowerValue?.toFixed(this.tolerance.precision)}`;

            case ToleranceType.LIMITS:
                const upper = this._value + (this.tolerance.upperValue || 0);
                const lower = this._value - (this.tolerance.lowerValue || 0);
                return `${upper.toFixed(this.tolerance.precision)} / ${lower.toFixed(this.tolerance.precision)}`;

            case ToleranceType.FIT:
                return this.tolerance.fitClass || '';

            default:
                return '';
        }
    }

    // ========================================================================
    // STATIC FACTORY METHODS
    // ========================================================================

    /**
     * Linear dimension oluştur
     */
    public static createLinear(
        start: THREE.Vector3,
        end: THREE.Vector3,
        options: Partial<CatiaDimensionOptions> = {}
    ): FikoCatiaDimension {
        return new FikoCatiaDimension(start, end, {
            type: CatiaDimensionType.LINEAR,
            ...options
        });
    }

    /**
     * Diameter dimension oluştur
     */
    public static createDiameter(
        center: THREE.Vector3,
        radius: number,
        angle: number = 0,
        options: Partial<CatiaDimensionOptions> = {}
    ): FikoCatiaDimension {
        const start = center.clone().add(
            new THREE.Vector3(Math.cos(angle), Math.sin(angle), 0).multiplyScalar(-radius)
        );
        const end = center.clone().add(
            new THREE.Vector3(Math.cos(angle), Math.sin(angle), 0).multiplyScalar(radius)
        );

        return new FikoCatiaDimension(start, end, {
            type: CatiaDimensionType.DIAMETER,
            style: { prefix: 'Ø' },
            ...options
        });
    }

    /**
     * Radius dimension oluştur
     */
    public static createRadius(
        center: THREE.Vector3,
        radius: number,
        angle: number = 0,
        options: Partial<CatiaDimensionOptions> = {}
    ): FikoCatiaDimension {
        const end = center.clone().add(
            new THREE.Vector3(Math.cos(angle), Math.sin(angle), 0).multiplyScalar(radius)
        );

        return new FikoCatiaDimension(center, end, {
            type: CatiaDimensionType.RADIUS,
            style: { prefix: 'R' },
            ...options
        });
    }

    /**
     * Horizontal dimension oluştur
     */
    public static createHorizontal(
        start: THREE.Vector3,
        end: THREE.Vector3,
        options: Partial<CatiaDimensionOptions> = {}
    ): FikoCatiaDimension {
        return new FikoCatiaDimension(start, end, {
            type: CatiaDimensionType.HORIZONTAL,
            ...options
        });
    }

    /**
     * Vertical dimension oluştur
     */
    public static createVertical(
        start: THREE.Vector3,
        end: THREE.Vector3,
        options: Partial<CatiaDimensionOptions> = {}
    ): FikoCatiaDimension {
        return new FikoCatiaDimension(start, end, {
            type: CatiaDimensionType.VERTICAL,
            ...options
        });
    }

    /**
     * Toleranslı dimension oluştur
     */
    public static createWithTolerance(
        start: THREE.Vector3,
        end: THREE.Vector3,
        upperTolerance: number,
        lowerTolerance: number,
        options: Partial<CatiaDimensionOptions> = {}
    ): FikoCatiaDimension {
        return new FikoCatiaDimension(start, end, {
            tolerance: {
                type: ToleranceType.ASYMMETRIC,
                upperValue: upperTolerance,
                lowerValue: lowerTolerance
            },
            ...options
        });
    }
}

// ============================================================================
// CATIA DIMENSION MANAGER
// ============================================================================

/**
 * CATIA Dimension Manager - Birden fazla ölçüyü yönetir
 */
export class CatiaDimensionManager {
    private dimensions: Map<string, FikoCatiaDimension> = new Map();
    private scene: THREE.Scene | THREE.Group;
    private defaultStyle: Partial<CatiaStyleConfig> = {};

    constructor(scene: THREE.Scene | THREE.Group) {
        this.scene = scene;
    }

    /**
     * Varsayılan stili ayarla
     */
    public setDefaultStyle(style: Partial<CatiaStyleConfig>): void {
        this.defaultStyle = style;
    }

    /**
     * Ölçü ekle
     */
    public add(id: string, dimension: FikoCatiaDimension): void {
        if (this.dimensions.has(id)) {
            this.remove(id);
        }
        this.dimensions.set(id, dimension);
        this.scene.add(dimension);
    }

    /**
     * Ölçü kaldır
     */
    public remove(id: string): void {
        const dim = this.dimensions.get(id);
        if (dim) {
            dim.dispose();
            this.scene.remove(dim);
            this.dimensions.delete(id);
        }
    }

    /**
     * Tüm ölçüleri kaldır
     */
    public clear(): void {
        this.dimensions.forEach((dim, id) => {
            dim.dispose();
            this.scene.remove(dim);
        });
        this.dimensions.clear();
    }

    /**
     * Linear dimension oluştur ve ekle
     */
    public createLinear(
        id: string,
        start: THREE.Vector3,
        end: THREE.Vector3,
        options: Partial<CatiaDimensionOptions> = {}
    ): FikoCatiaDimension {
        const dim = FikoCatiaDimension.createLinear(start, end, {
            style: { ...this.defaultStyle, ...options.style },
            ...options
        });
        this.add(id, dim);
        return dim;
    }

    /**
     * Diameter dimension oluştur ve ekle
     */
    public createDiameter(
        id: string,
        center: THREE.Vector3,
        radius: number,
        angle: number = 0,
        options: Partial<CatiaDimensionOptions> = {}
    ): FikoCatiaDimension {
        const dim = FikoCatiaDimension.createDiameter(center, radius, angle, {
            style: { ...this.defaultStyle, ...options.style },
            ...options
        });
        this.add(id, dim);
        return dim;
    }

    /**
     * Ölçüyü al
     */
    public get(id: string): FikoCatiaDimension | undefined {
        return this.dimensions.get(id);
    }

    /**
     * Tüm ölçüleri al
     */
    public getAll(): FikoCatiaDimension[] {
        return Array.from(this.dimensions.values());
    }

    /**
     * Temizlik
     */
    public dispose(): void {
        this.clear();
    }
}

// CATIA_STYLE alias for backward compatibility
export const CATIA_STYLE = CATIA_DEFAULT_STYLE;
